// @flow
import { Text, View } from 'reshaped';

import { selectCartReselleeID } from '@gandi/cart';

import { useAddress } from '@gandi/react-places';
import type { Organization } from '@gandi/redux-organization';
import type { Organization as SharingOrganization } from '@gandi/redux-sharing';
import { useTranslator } from '@gandi/translate';

import { useCartSummaryContext } from 'src/Layout/context/CartSummary';
import { Contact, type OrganizationDetail } from 'src/Reshaped/Contact';
import { ErrorBoundary } from 'src/helpers/ErrorBoundary';
import { useAppSelector } from 'src/hooks/useAppSelector';
import { useMissingOrgInfo } from 'src/hooks/useMissingOrgInfo';
import { useOrganization } from 'src/hooks/useOrganization';

import type { ListCustomer } from '../../Customer/types';

export type AddressProps = $Exact<{
  orga: Organization | OrganizationDetail | SharingOrganization | ListCustomer,
  checkMissing?: boolean,
  checkMissingVAT?: boolean,
  type?: 'organization' | 'customer',
}>;

function BaseAddress({
  checkMissing = true,
  checkMissingVAT = false,
  orga,
  type,
}: AddressProps): React.MixedElement {
  const { address, recipient } = useAddress({
    data: orga,
    type,
    showRecipient: false,
  });

  const { missingInfo } = useMissingOrgInfo(
    orga,
    checkMissing,
    checkMissingVAT,
  );

  return (
    <View
      direction="column"
      attributes={{ style: { wordBreak: 'break-word' } }}
      gap={{
        s: 1,
        m: 0,
      }}
    >
      <Text variant="body-2">{recipient}</Text>
      <Text variant="body-2">{orga.email}</Text>
      {address && (
        <Text wrap="balance" variant="body-2">
          {address}
        </Text>
      )}
      {checkMissing &&
        !!missingInfo?.length &&
        missingInfo.map(({ color, msg }) => (
          <Text key={msg} color={color || 'neutral-faded'} variant="body-2">
            {msg}
          </Text>
        ))}
    </View>
  );
}

export function Address(props: AddressProps): React.MixedElement {
  const { __ } = useTranslator();

  return (
    <ErrorBoundary fallback={<Text color="critical">{__('Error')}</Text>}>
      <BaseAddress {...props} />
    </ErrorBoundary>
  );
}

type Props = {
  ownerType?: boolean,
  isCustomerBlock?: boolean,
};
export function ContactBlock({
  isCustomerBlock,
  ownerType,
}: Props): React.MixedElement {
  const { __ } = useTranslator();

  const { cartOwnerOrganization, productOwnerOrganization } =
    useCartSummaryContext();

  const cartCustomerId = useAppSelector(selectCartReselleeID);
  const { profile: cartCustomer } = useOrganization(cartCustomerId);

  const displayedProductOwner =
    (cartCustomerId && cartCustomer) || productOwnerOrganization;

  const currentOrga = ownerType ? displayedProductOwner : cartOwnerOrganization;

  if (!currentOrga) {
    return (
      <View>
        <Text weight="bold" variant="body-2">
          {__('Choose or create')}
        </Text>
      </View>
    );
  }

  const isResellee =
    'entity_type' in currentOrga && currentOrga.entity_type === 'resellee';

  return (
    <View gap={2} shrink>
      <Contact
        {...currentOrga}
        isContactBlock
        isCustomerBlock={isCustomerBlock}
      />
      <Address
        orga={currentOrga}
        checkMissing={!isResellee}
        checkMissingVAT={!ownerType}
      />
    </View>
  );
}
